import type { OutreachNiche } from '@/api/types';

interface NichesPickerProps {
  /** All niches available for selection. Caller filters by category if needed. */
  niches: OutreachNiche[];
  /** Currently selected niche codes (order preserved). */
  value: string[];
  /** Called with the next array of selected codes when the user toggles a chip. */
  onChange: (next: string[]) => void;
  /**
   * When false, selecting a chip replaces the current selection instead of
   * adding to it. Use for teams with `multipleNichesAllowed === false`.
   */
  multiple?: boolean;
  /** Loading state for the niche list — renders a placeholder skeleton row. */
  loading?: boolean;
  /** Optional className passthrough for layout/width control. */
  className?: string;
  /** Optional disabled flag — chips render but don't respond to clicks. */
  disabled?: boolean;
  /**
   * Names for selected codes that aren't in `niches` (a hidden niche, or one
   * outside this creator type). They render as removable chips after the
   * options so an edit never drops them silently; without this the raw code
   * shows.
   */
  nameOf?: (code: string) => string;
}

/**
 * Reusable chip-style niche selector. Same visual language as LogInvite's
 * picker but stripped of the bonus / focus / sort-order logic — those are
 * create-time concerns. Use this anywhere we re-render an editor for an
 * existing invite's niches (admin Edit dialog, outreacher row-level edit).
 *
 * The caller owns the list of niches passed in (already category-filtered
 * if applicable). When `multiple === false`, picking a chip overwrites the
 * selection rather than appending — matches the per-team
 * `multipleNichesAllowed` rule the backend enforces.
 */
export function NichesPicker({
  niches,
  value,
  onChange,
  multiple = true,
  loading = false,
  className,
  disabled = false,
  nameOf,
}: NichesPickerProps): JSX.Element {
  const offered = new Set(niches.map((n) => n.code));
  // Already on the invite but no longer pickable (hidden, or out of scope).
  // Shown so they can be seen and removed; once removed they can't be re-added.
  const retained = value.filter((code) => !offered.has(code));
  function toggle(code: string): void {
    if (disabled) return;
    if (!multiple) {
      onChange(value.includes(code) ? [] : [code]);
      return;
    }
    const next = value.includes(code)
      ? value.filter((n) => n !== code)
      : [...value, code];
    onChange(next);
  }

  if (loading) {
    return (
      <div className={className ?? 'flex flex-wrap gap-1.5'} aria-busy>
        {Array.from({ length: 4 }).map((_, i) => (
          <span
            key={i}
            className="inline-block h-6 w-16 animate-pulse rounded-full bg-muted"
          />
        ))}
      </div>
    );
  }

  if (niches.length === 0 && retained.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">No niches available.</p>
    );
  }

  return (
    <div className={className ?? 'flex flex-wrap gap-1.5'}>
      {niches.map((n) => {
        const active = value.includes(n.code);
        const chipClass = active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-transparent text-foreground hover:bg-muted';
        return (
          <button
            key={n.id}
            type="button"
            disabled={disabled}
            onClick={() => toggle(n.code)}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${chipClass}`}
            title={active ? 'Click to remove' : 'Click to add'}
          >
            <span>{n.displayName}</span>
            {n.isFocused && (
              <span
                aria-label="Focus niche"
                className={
                  active
                    ? 'text-[10px] text-primary-foreground/90'
                    : 'text-[10px] text-warning'
                }
              >
                ★
              </span>
            )}
          </button>
        );
      })}
      {retained.map((code) => (
        <button
          key={`retained-${code}`}
          type="button"
          disabled={disabled}
          onClick={() => toggle(code)}
          aria-pressed
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-primary px-3 py-1 text-xs text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          title="Not offered any more (hidden, or not for this creator type). Click to remove; it can't be added back."
        >
          <span>{nameOf ? nameOf(code) : code}</span>
          <span className="text-[10px] text-muted-foreground">not offered</span>
        </button>
      ))}
    </div>
  );
}
